import React, { useEffect, useMemo, useState } from 'react';
import { useAnalytics, useCart } from '@chordcommerce/gatsby-theme-autonomy';
import { renderRichText } from 'gatsby-source-contentful/rich-text';
import { GatsbyImage } from 'gatsby-plugin-image';

import { Wrapper } from '~/styles/Wrapper.styled';
import { Section } from '~/styles/Section.styled';

import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';
import { useCartContext } from '~/components/CartContext/CartContext';
import { DisplayPrice } from '~/components/DisplayPrice/DisplayPrice';
import { Benefits } from '~/components/Product/Benefits/Benefits';
import { FeaturedProducts } from '~/components/FeaturedProducts/FeaturedProducts';
import { FeaturedKits } from '~/components/FeaturedKits/FeaturedKits';
import { SubscriptionTooltip } from '~/components/Product/SubscriptionTooltip/SubscriptionTooltip';
import { Testimonials } from '~/components/Product/Testimonials/Testimonials';
import { Ingredients } from '~/components/Product/Ingredients/Ingredients';
import { YotpoReviews } from '~/components/Product/YotpoReviews/YotpoReviews';
import { YotpoStarRatings } from '~/components/Product/YotpoStarRatings/YotpoStarRatings';
import { SingleFeaturedProduct } from '~/components/SingleFeaturedProduct/SingleFeaturedProduct';
import { Breadcrumbs } from '~/components/Generic/Breadcrumbs/Breadcrumbs';
import { FaqBlock } from '~/components/Generic/FaqBlock/FaqBlock';
import { Dropdown } from '~/components/Generic/Dropdown/Dropdown';
import { Cta } from '~/components/Generic/Cta/Cta';
import ShoppingBagIcon from '~/assets/images/icons/shoppingbag-white.svg';
import { UpsellCarousel } from '~/components/UpsellCarousel/UpsellCarousel';

import { StyledProductPage } from './ProductPage.style';
import { IProduct, Kit, IntentSlug } from '~/types/types.d';
import { intentSlugs } from '~/types/constants';
import { Helmet } from 'react-helmet';
import useUser from '~/hooks/components/use-user';
import { SwiperOptions, Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { useMediaQuery } from '@mantine/hooks';
import 'swiper/css';
import 'swiper/css/pagination';

const SWIPER_OPTS: SwiperOptions = {
  modules: [Pagination],
  pagination: true,
  slidesPerView: 'auto',
  spaceBetween: 10,
  updateOnWindowResize: true
}

const BUY_OPTION_SLUG = 'buy';

export const ProductPage: React.FC<PageProps> = ({ product }) => {
  const { user } = useUser();
  const isAmbassador = user?.data?.roles?.includes('ambassador');
  const ambassadorDiscount = user?.data?.roles?.includes('New Ambassador (30% Off)') ? 0.7 : 0.8;
  
  const isMobile = useMediaQuery('(max-width: 768px)');
  const { addToCart, isEnrolmentInCart } = useCartContext();
  const { cart } = useCart();

  const { trackProductViewed } = useAnalytics();
  const [aggregateRating, setAggregateRating] = useState<any>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const targetNode = document.head;
    const config = { childList: true };
    const onReviewSnippetAdded = (mutationList: MutationRecord[], observer: MutationObserver) => {
      for (const mutation of mutationList) {
        if (mutation.type === 'childList') {
          for(const node of (mutation.addedNodes || [])) {
            if (node.nodeName === 'SCRIPT' && (node as HTMLScriptElement).classList.contains('y-rich-snippet-script')) {
              let jsonData: any = {};
              try {
                jsonData = JSON.parse((node as HTMLScriptElement).innerHTML);
              } catch (e) {
                console.error(e);
              }
              if (jsonData && jsonData['@type'] === 'Product' && jsonData.aggregateRating) {
                setAggregateRating(jsonData.aggregateRating);
              }
              (node as HTMLScriptElement).remove();
            }
          }
        }
      }
    };
    const observer = new MutationObserver(onReviewSnippetAdded);
    observer.observe(targetNode, config);
    return () => {
      observer.disconnect();
    }
  }, [])

  useEffect(() => {
    trackProductViewed({
      product: {
        sku: product.slug,
        ...product,
      }
    });
  }, [])

  const subscription = isAmbassador
    ? product.subscriptionForAmbassadors
    : product.subscription;
  
  const { intervals: frequencies, tooltip } = subscription || {};
  const sku = product?.sku || (product as IProduct)?.variants?.[0]?.sku || '';

  const cartLineItem = cart.data?.lineItems?.find(
    item => item.variant.sku === sku
  );
  const isItemInCart = !!cartLineItem;
  const isItemInCartASubscription = !!cartLineItem?.subscriptionLineItems.length;
  
  const intentCollection = product.collection?.find(col =>
    intentSlugs.includes(col.slug as IntentSlug)
  );

  const frequenciesDropdownOptions = frequencies?.map(f => {
    return { label: f.name, value: f.slug };
  }) || [];
  frequenciesDropdownOptions.unshift({
    label: 'Buy once',
    value: BUY_OPTION_SLUG,
  });
  const [frequency, setFrequency] = useState(frequencies?.[0] || null);
  const selectedFrequencyOption = frequenciesDropdownOptions.find(
    fo => fo.value === (!frequency ? BUY_OPTION_SLUG : frequency.slug)
  );

  const handleChangeFrequency = (slug: string) => {
    const newFrequency = frequencies.find(f => f.slug === slug);
    setFrequency(newFrequency);
  };
  
  const regularPrice =
    product.__typename === 'ContentfulProduct'
      ? product.variants[0].price
      : product.price;
  const discountedPrice = isAmbassador ? regularPrice * ambassadorDiscount : (frequency
    ? regularPrice * ((100 - subscription.discountPercentage) / 100)
    : false)
  const { openCart } = useCartContext();
  
  const scrollTo = (
    id: string,
    options: ScrollIntoViewOptions = { behavior: 'smooth', block: "center" }
  ) => {
    document.getElementById(id).scrollIntoView(options);
  };


  const schema = useMemo(() => {
    const richtextDocument = product.description?.raw ? JSON.parse(product.description.raw) : undefined;
    return {
      '@context': 'https://schema.org',
      '@type': 'Product',
      description: richtextDocument ? documentToPlainTextString(richtextDocument) : undefined,
      name: product.name,
      image: product.mainImage.gatsbyImageData?.images?.fallback?.src,
      aggregateRating: aggregateRating || undefined,
      offers: {
        '@type': 'Offer',
        availability: 'https://schema.org/InStock',
        price: discountedPrice ? discountedPrice : regularPrice,
        priceCurrency: 'USD',
      },
    };
  }, [product, discountedPrice, regularPrice, aggregateRating]);
  const productCollection = product.collection?.find( col => col.slug !== 'all' && col.productType) || {
    name: 'All Products',
    slug: 'all',
    productType: true
  };

  const cartLabel =
    isItemInCart && !!isItemInCartASubscription === !!frequency
      ? 'In Cart'
      : 'Add to Cart';
  
  const isUserAllowedToViewPage = !product.ambassadorOnly || isAmbassador;
  return !isUserAllowedToViewPage ? null : (
    <StyledProductPage intentSlug={intentCollection?.slug as IntentSlug}>
      <Helmet>
        <script type="application/ld+json" className='productPage__schema'>{JSON.stringify(schema)}</script>
      </Helmet>
      <Breadcrumbs
        crumbs={[
          {
            href: '/',
            label: 'Home',
          },
          {
            href: `/shop/${
              productCollection.slug === 'all'
                ? ''
                : `${productCollection.slug}/all/`
            }`,
            label: productCollection.name,
          },
          {
            href: `/products/${product.slug}`,
            label: product.name,
          },
        ]}
      />

      <div className="product">
        <div className="thumbnails">
          {product.mainImage && (
            <figure onClick={() => scrollTo('mainImage')}>
              <GatsbyImage
                image={product.mainImage.gatsbyImageData}
                alt={product.mainImage.title}
              />
            </figure>
          )}

          {product.images?.map(image => (
            <figure
              key={`image-${image.id}`}
              onClick={() => scrollTo(image.id)}
            >
              <GatsbyImage image={image.gatsbyImageData} alt={image.title} />
            </figure>
          ))}
        </div>
        <Wrapper gutter width="normal">
          {!isMobile && (
            <div className="gallery">
              {product.mainImage && (
                <figure>
                  <GatsbyImage
                    image={product.mainImage.gatsbyImageData}
                    alt={product.mainImage.title}
                    id="mainImage"
                  />
                </figure>
              )}

              {product.images?.map(image => (
                <figure key={`image-${image.id}`} id={image.id}>
                  <GatsbyImage
                    image={image.gatsbyImageData}
                    alt={image.title}
                  />
                </figure>
              ))}
            </div>
          )}

          {isMobile && (
            <Swiper className="gallery" {...SWIPER_OPTS}>
              {product.mainImage && (
                <SwiperSlide>
                  <figure>
                    <GatsbyImage
                      image={product.mainImage.gatsbyImageData}
                      alt={product.mainImage.title}
                      id="mainImage"
                    />
                  </figure>
                </SwiperSlide>
              )}

              {product.images?.map(image => (
                <SwiperSlide key={`image-${image.id}`}>
                  <figure id={image.id}>
                    <GatsbyImage
                      image={image.gatsbyImageData}
                      alt={image.title}
                    />
                  </figure>
                </SwiperSlide>
              ))}
            </Swiper>
          )}

          <div className="details">
            {intentCollection && (
              <div className="category">{intentCollection.name}</div>
            )}

            <h2>{product.name}</h2>
            <div className="description">
              {product.description && renderRichText(product.description, {})}
            </div>

            <div className="price-options">
              <DisplayPrice
                size="large"
                finalAmount={
                  discountedPrice ? `${discountedPrice.toFixed(2)}` : null
                }
                originalAmount={`${regularPrice}`}
              />
              <div className="frequency">
                <Dropdown
                  label="Buy Option"
                  value={selectedFrequencyOption}
                  onReset={() => false}
                  onChange={v => handleChangeFrequency(v.value)}
                  options={frequenciesDropdownOptions}
                />
              </div>
              <div className="info">
                <SubscriptionTooltip>
                  {tooltip && renderRichText(tooltip, {})}
                </SubscriptionTooltip>
              </div>
            </div>

            <div className="cart-rating">
              <Cta
                href="#"
                variant="primary"
                rightIcon={<ShoppingBagIcon />}
                disabled={isEnrolmentInCart}
                onClick={() => addToCart(sku, 1, !!frequency, frequency)}
              >
                {cartLabel}
              </Cta>
              <YotpoStarRatings slug={product.slug} />
            </div>

            {product.pairings?.length > 0 && (
              <div className="related-products">
                <UpsellCarousel
                  products={product.pairings}
                  onItemAdd={openCart}
                />
              </div>
            )}
            {product.productQuickDetails?.length > 0 && (
              <div className="details">
                <FaqBlock questions={product.productQuickDetails} generateSchema={false} />
              </div>
            )}
          </div>
        </Wrapper>
      </div>

      {product.benefits?.length && (
        <Section bg="green" color="white" className="benefits">
          <Wrapper gutter={false} width="normal">
            <Benefits sections={product.benefits} productName={product.name} />
          </Wrapper>
        </Section>
      )}

      {product.quotes?.length > 0 && (
        <Section bg="green">
          <Wrapper>
            <Testimonials testimonials={product.quotes} />
          </Wrapper>
        </Section>
      )}

      <Section>
        <Wrapper width="normal">
          <YotpoReviews
            sku={sku}
            slug={product.slug}
            productName={product.name}
            price={`${regularPrice}`}
            imageUrl={product.mainImage?.gatsbyImageData?.images?.fallback?.src}
          />
        </Wrapper>
      </Section>

      {product.__typename === 'ContentfulProduct' &&
        product?.ingredients?.length > 0 && (
          <Section>
            <Ingredients
              title="What’s inside?"
              ingredients={product.ingredients}
            />
          </Section>
        )}

      {product.singleFeaturedProduct && (
        <SingleFeaturedProduct section={product.singleFeaturedProduct} />
      )}

      {product.productFaqQuestions?.length > 0 && (
        <div className="need-to-know">
          <Wrapper width="normal">
            <h3>All you need to know</h3>
            <FaqBlock questions={product.productFaqQuestions} />
          </Wrapper>
        </div>
      )}

      {product.relatedProducts?.length > 0 && (
        <Section>
          <Wrapper>
            <FeaturedProducts
              title="You may also like"
              products={product.relatedProducts}
            />
          </Wrapper>
        </Section>
      )}

      <Section bg="green">
        <Wrapper>
          <FeaturedKits />
        </Wrapper>
      </Section>
    </StyledProductPage>
  );
};

interface PageProps {
  product: { __typename: string } & (IProduct | Kit);
}
